import { AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react';
import { Progress } from '../ui/progress';
import { cn } from '../ui/utils';

interface BudgetProgressBarProps {
  /**
   * Current spent amount
   */
  current: number;
  /**
   * Total budget amount
   */
  total: number;
  /**
   * Show percentage and status labels
   * @default true
   */
  showLabel?: boolean;
  /**
   * Show status icon
   * @default true
   */
  showIcon?: boolean;
  /**
   * Size variant
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * BudgetProgressBar - Progress bar with traffic light status indicators
 * 
 * Visual representation of budget consumption with color-coded thresholds:
 * - Green (0-75%): Safe
 * - Yellow (75-90%): Warning
 * - Orange (90-100%): Danger
 * - Red (100%+): Over budget
 * 
 * @example
 * ```tsx
 * <BudgetProgressBar
 *   current={8500}
 *   total={10000}
 *   size="md"
 *   showLabel
 *   showIcon
 * />
 * ```
 */
export function BudgetProgressBar({
  current,
  total,
  showLabel = true,
  showIcon = true,
  size = 'md',
  className,
}: BudgetProgressBarProps) {
  const percent = total > 0 ? Math.min((current / total) * 100, 100) : 0;
  
  const status = percent >= 100 ? 'over' : percent >= 90 ? 'danger' : percent >= 75 ? 'warning' : 'safe';

  const statusConfig = {
    safe: {
      color: 'bg-success-500',
      icon: CheckCircle,
      textColor: 'text-success-700 dark:text-success-400',
      label: 'Dentro do orçamento',
    },
    warning: {
      color: 'bg-warning-500',
      icon: AlertTriangle,
      textColor: 'text-warning-700 dark:text-warning-400',
      label: 'Atenção',
    },
    danger: {
      color: 'bg-danger-500',
      icon: AlertTriangle,
      textColor: 'text-danger-700 dark:text-danger-400',
      label: 'Crítico',
    },
    over: {
      color: 'bg-danger-600',
      icon: AlertCircle,
      textColor: 'text-danger-700 dark:text-danger-400',
      label: 'Estourado',
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  const sizeClasses = {
    sm: 'h-1.5',
    md: 'h-2',
    lg: 'h-3',
  };

  const iconSizeClasses = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  return (
    <div className={cn('space-y-2', className)}>
      {showLabel && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {showIcon && <Icon className={cn(iconSizeClasses[size], config.textColor)} />}
            <span className={cn('text-sm', config.textColor)}>
              {percent.toFixed(1)}%
            </span>
          </div>
          <span className="text-xs text-muted-foreground">
            {config.label}
          </span>
        </div>
      )}
      
      <Progress 
        value={percent} 
        className={sizeClasses[size]}
        indicatorClassName={config.color}
      />
      
      {showLabel && (
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>R$ {current.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
          <span>R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</span>
        </div>
      )}
    </div>
  );
}
